'use client'

import { useTranslations } from 'next-intl'
import { Typography } from '@repo/design-system-cf/components/typography'
import { Link } from '@repo/web-ui/components/link'
import { Surface } from '@repo/rpc-types-creativefabrica-common-v1/surface/v1'
import { useAutoTopUpPromptBannerQuery } from '@repo/studio-components-prompt-banners/top-up/use-auto-top-up-prompt-banner-query'
import { PricingAction } from '@repo/studio-hooks-coins/enums'
import { usePriceQuery } from '@repo/studio-hooks-coins/use-price'
import { AppRoutes } from '@repo/web-utils/app-routes'

type MarketplaceAutoTopUpNoticeProps =
  | { pricingAction: PricingAction }
  | { nextGenerationCost: number | undefined; isNextGenerationCostPending: boolean }

export function MarketplaceAutoTopUpNotice(props: MarketplaceAutoTopUpNoticeProps) {
  const t = useTranslations('pages.tools_common')
  const priced = useNextGenerationCost(props)
  const { show, isPending } = useAutoTopUpPromptBannerQuery({
    ...priced,
    surface: Surface.MARKETPLACE,
  })

  if (isPending || !show) {
    return null
  }

  return (
    <Typography variant="body-compact-01" className="text-cf-grey-700">
      {t('auto_top_up_notice')}{' '}
      <Link href={AppRoutes.COINS_TOPUPS} component="a" target="_blank" rel="noopener noreferrer">
        {t('auto_top_up_configure')}
      </Link>
    </Typography>
  )
}

function useNextGenerationCost(props: MarketplaceAutoTopUpNoticeProps) {
  const priceQuery = usePriceQuery(
    'pricingAction' in props ? props.pricingAction : PricingAction.UNSPECIFIED,
    { enabled: 'pricingAction' in props },
  )

  if ('pricingAction' in props) {
    return {
      nextGenerationCost: priceQuery.data,
      isNextGenerationCostPending: priceQuery.isPending,
    }
  }

  return {
    nextGenerationCost: props.nextGenerationCost,
    isNextGenerationCostPending: props.isNextGenerationCostPending,
  }
}
